import React from 'react';
import { CheckCircle2, Crown, X } from 'lucide-react';
import {
    BREEDER_ELITE_MONTHLY, BREEDER_ELITE_YEARLY, BREEDER_PRO_MONTHLY, BREEDER_PRO_YEARLY, BREEDER_TRIAL_DAYS,
    ELITE_FEATURES, FREE_FEATURES, PAYMENT_METHODS, PRO_FEATURES, breederMailto, clp,
} from '../../data/breederPlans';

export interface BreederPlanInfo {
    plan: 'free' | 'pro' | 'elite';
    proUntil?: string | null;
    eliteUntil?: string | null;
    limits: { pets: number | null; litters: number | null; heats: number | null; photos: number; docs: number | null };
    usage: { pets: number; litters: number; heats: number };
}

/** Días que le quedan al plan de pago vigente (prueba o pago); null si es Gratis. */
export const proDaysLeft = (p?: BreederPlanInfo | null) => {
    if (!p || p.plan === 'free') return null;
    const until = p.plan === 'elite' ? p.eliteUntil : p.proUntil;
    if (!until) return null;
    const ms = new Date(String(until).replace(' ', 'T')).getTime() - Date.now();
    return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 86400000)) : null;
};

/**
 * Aviso cuando se topa con un límite del plan Gratis o se pide una función de un plan superior.
 * Mientras el cobro en línea no esté activo, "Quiero el plan…" abre un correo para activarlo.
 */
export const BreederUpgradeModal: React.FC<{ reason?: string; need?: 'pro' | 'elite'; onClose: () => void }> = ({ reason, need = 'pro', onClose }) => {
    const elite = need === 'elite';
    return (
        <div className="fixed inset-0 z-[160] flex items-end sm:items-center justify-center bg-slate-900/70 backdrop-blur-sm sm:p-4" onClick={onClose}>
            <div className="bg-white w-full sm:max-w-2xl rounded-t-[2.5rem] sm:rounded-[2.5rem] p-6 sm:p-8 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4 mb-5">
                    <div className="flex items-center gap-3">
                        <span className="w-12 h-12 bg-amber-400 rounded-[7px] flex items-center justify-center shadow-lg"><Crown size={22} className="text-white" /></span>
                        <div>
                            <h3 className="text-xl font-black text-slate-800">{elite ? 'Pasa a Criador Élite' : 'Pasa a Criador Pro'}</h3>
                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{elite ? 'Contratos y lista de espera' : 'Sin límites para tu criadero'}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700" aria-label="Cerrar"><X size={20} /></button>
                </div>

                {reason && <p className="text-sm font-bold text-amber-700 bg-amber-50 border border-amber-100 rounded-[7px] px-4 py-3 mb-5">{reason}</p>}

                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    <div className={`rounded-[7px] p-5 ${elite ? 'border border-slate-200' : 'border border-slate-200'}`}>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{elite ? 'Criador Pro' : 'Gratis'}</p>
                        <p className="text-2xl font-black text-slate-800 mb-3">{elite ? clp(BREEDER_PRO_MONTHLY) : '$0'}{elite && <span className="text-sm font-bold text-slate-400">/mes</span>}</p>
                        <ul className="space-y-2">{(elite ? PRO_FEATURES : FREE_FEATURES).map(f => <li key={f} className="flex gap-2 text-[12px] font-medium text-slate-600"><CheckCircle2 size={14} className="text-slate-300 shrink-0 mt-0.5" />{f}</li>)}</ul>
                    </div>
                    <div className="rounded-[7px] bg-orange-500 text-white p-5 shadow-xl shadow-orange-500/20">
                        <p className="text-[10px] font-black text-orange-100 uppercase tracking-widest">{elite ? 'Criador Élite' : 'Criador Pro'}</p>
                        <p className="text-2xl font-black">{clp(elite ? BREEDER_ELITE_MONTHLY : BREEDER_PRO_MONTHLY)}<span className="text-sm font-bold text-orange-100">/mes</span></p>
                        <p className="text-[11px] font-bold text-orange-100 mb-3">o {clp(elite ? BREEDER_ELITE_YEARLY : BREEDER_PRO_YEARLY)} al año</p>
                        <ul className="space-y-2">{(elite ? ELITE_FEATURES : PRO_FEATURES).map(f => <li key={f} className="flex gap-2 text-[12px] font-medium"><CheckCircle2 size={14} className="text-amber-200 shrink-0 mt-0.5" />{f}</li>)}</ul>
                    </div>
                </div>

                <p className="text-[11px] font-bold text-slate-500 text-center mb-3">Medios de pago en Chile: {PAYMENT_METHODS.map(m => m.name).join(' · ')}</p>
                <a href={breederMailto(elite ? 'Élite' : 'Pro')} className="block w-full text-center py-4 bg-slate-900 hover:bg-orange-500 text-white rounded-[7px] font-black uppercase text-xs tracking-widest transition-colors">Quiero el plan {elite ? 'Élite' : 'Pro'}</a>
                <p className="text-[10px] font-medium text-slate-400 text-center mt-3 leading-relaxed">Te escribimos para activarlo. No borramos nada de lo que ya registraste. Prueba de {BREEDER_TRIAL_DAYS} días de Pro al registrarte.</p>
            </div>
        </div>
    );
};
